import { Lock, WebGlobe, People } from '@withjoy/joykit/icons';
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { ScheduleSection } from '../ScheduleSection';
import { Flex, TextV2, styled } from '@withjoy/joykit';
import { useTranslation } from '@shared/core/i18n';
import { FieldMetadata } from '@shared/components/ScheduleEditor/hooks/useScheduleFormState';
import type { ScheduleEditorData, ScheduleTag } from '@shared/components/ScheduleEditor/ScheduleEditor.types';
import { TagsInput } from '@shared/components/TagsInput';
import { useGetTagsByEventQuery } from '@graphql/generated';
import { Skeleton } from '@shared/components/Skeleton';

type PrivacyMode = 'public' | 'specific';

type EventPrivacyInlineSectionProps = Readonly<{
  state: ScheduleEditorData;
  setValue: (key: keyof ScheduleEditorData, value: ReadonlyArray<ScheduleTag>) => void;
  meta: Record<keyof ScheduleEditorData, FieldMetadata>;
  eventId: string;
}>;

const OptionCard = styled(Flex)<{ $isSelected: boolean }>`
  padding: 12px 16px;
  border: 1px solid ${({ theme, $isSelected }) => ($isSelected ? theme.colors.newUIBrandPrimary : theme.colors.mono3)};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  background-color: ${({ theme, $isSelected }) => ($isSelected ? 'rgba(73, 81, 239, 0.05)' : theme.colors.white)};

  &:hover {
    border-color: ${({ theme, $isSelected }) => ($isSelected ? theme.colors.newUIBrandPrimary : theme.colors.mono5)};
  }
`;

const IconWrapper = styled(Flex)`
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.mono14};
`;

export const EventPrivacyInlineSection: React.FC<EventPrivacyInlineSectionProps> = ({ state, setValue, eventId }) => {
  const { tq } = useTranslation('scheduleEditor');
  const [mode, setMode] = useState<PrivacyMode>(state.tags.length > 0 ? 'specific' : 'public');

  // Fetch available tags for the event (options for the dropdown)
  const { data: availableTagsData, loading: availableTagsLoading } = useGetTagsByEventQuery({
    variables: { eventId },
    batchMode: 'fast',
  });

  // Map API tags to TagsInput format
  const availableTagOptions = useMemo(() => {
    return availableTagsData?.getTagsByEvent ?? [];
  }, [availableTagsData]);

  // Sync mode with state.tags when it changes externally
  useEffect(() => {
    setMode(state.tags.length > 0 ? 'specific' : 'public');
  }, [state.tags]);

  const handleModeChange = useCallback(
    (newMode: PrivacyMode) => {
      setMode(newMode);
      if (newMode === 'public') {
        // Clear tags when switching to public
        setValue('tags', []);
      }
    },
    [setValue],
  );

  const handleTagsChange = useCallback(
    (newTags: ReadonlyArray<ScheduleTag>) => {
      setValue('tags', newTags);
    },
    [setValue],
  );

  return (
    <ScheduleSection
      icon={<Lock size={24} />}
      title={tq`Event Privacy`}
    >
      <Flex
        flexDirection="column"
        gap="12px"
      >
        <TextV2
          typographyVariant="body2"
          color="mono10"
        >
          {tq`Who should be able to see this item on your schedule?`}
        </TextV2>
        <OptionCard
          $isSelected={mode === 'public'}
          onClick={() => handleModeChange('public')}
          alignItems="center"
          gap="12px"
        >
          <IconWrapper>
            <WebGlobe size={20} />
          </IconWrapper>
          <TextV2 typographyVariant="body1">{tq`Anyone who visits your website`}</TextV2>
        </OptionCard>

        <OptionCard
          $isSelected={mode === 'specific'}
          onClick={() => handleModeChange('specific')}
          alignItems="center"
          gap="12px"
        >
          <IconWrapper>
            <People size={20} />
          </IconWrapper>
          <TextV2 typographyVariant="body1">{tq`Specific guests`}</TextV2>
        </OptionCard>

        {mode === 'specific' && (
          <Flex
            flexDirection="column"
            gap="8px"
            paddingLeft="36px"
          >
            <TextV2
              typographyVariant="body2"
              color="mono5"
            >
              {tq`Select tags to show only to these guests`}
            </TextV2>
            {availableTagsLoading ? (
              <Skeleton
                width="100%"
                height={36}
                borderRadius="6px"
              />
            ) : (
              <TagsInput
                options={availableTagOptions}
                value={state.tags}
                onChange={handleTagsChange}
                placeholder={tq`Add tags...`}
              />
            )}
          </Flex>
        )}
      </Flex>
    </ScheduleSection>
  );
};
